<template>
  <transition name="fade">
    <div class="container" v-if="community">
      <div class="tile-content"
           :class="community.type.toLowerCase() === 'private' ? 'private' : ''"
           @click="goToCommunity()"
      >
        <div class="tile-header">
          <div class="private-icon-container">
            <img v-if="community.type.toLowerCase() === 'private'"
                 src="@/assets/PrivateCommunityIcon.svg" alt="private-community-icon"
            />
            <img v-if="community.isBroadcast"
                 src="@/assets/RSS.svg" alt="broadcast-community-icon"
            />
            <img v-if="community.isMember" :title="$t('user.userCommunities.joined')"
                 src="@/assets/Member.svg" alt="member-community-icon"
            />
          </div>
          <div class="title" :class="isReportedCommunity ? 'reported' : ''">
            <span>{{ community.name }}</span>
          </div>
          <div v-if="!isReportedCommunity">
            <ActionsMenu>
              <ReportCommunityModal :community-id="community.id" @click.stop />
              <NameOwnerModal @click.stop v-if="isAdministrator"
                              :community-id="community.id"
                              :current-owner-id="community.owner ? community.owner.id : undefined"
              />
            </ActionsMenu>
          </div>
          <div v-else>
            <AdminCommunityActionsMenu :community="community" @click.stop />
          </div>
        </div>
        <span class="description" :class="isReportedCommunity ? 'reported' : ''">
          {{ communityDescription(community.description) }}</span>
        <div v-if="community.owner" class="owner-info">
          <div class="photo-container">
            <user-photo class="user-photo owner-photo" :user="community.owner" />
          </div>
          <div class="info">
            <div class="top">
              <span>{{
                $t('community.communityTile.creationInfo', { createdOn: dateParser(community.createdAt) })
              }}</span>
            </div>
            <div class="bottom">
              <span>{{ community.owner.firstName }} {{ community.owner.lastName }}</span>
            </div>
          </div>
        </div>
        <div class="footer">
          <div v-if="!hasCommunityAccess() && hasJoinRequest" class="community-join">
            {{ $t('community.communityTile.joinRequestSent') }}
          </div>

          <div v-if="!hasCommunityAccess() && !hasJoinRequest" class="community-join">
            <span>{{ $t('community.communityTile.joinPrivateCommunity') }}</span>
            <v-button :on-click="addJoinRequest">
              {{ $t('community.communityTile.sendRequest') }}
            </v-button>
          </div>

          <v-image v-if="community && community.idImage"
                   class="community-illustration"
                   :image-id="community.idImage"
          />
          <img v-else
               class="community-illustration"
               src="@/assets/DefaultCommunityVisual.png"
               alt="default-image-visual"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import VImage from '@/components/shared/VImage'
import UserPhoto from '@/components/shared/UserPhoto'
import VButton from '@/components/shared/VButton'
import ClickOutside from 'vue-click-outside'
import { format } from 'date-fns'
import AdminCommunityActionsMenu from '../administrator/AdminCommunityActionsMenu'
import ActionsMenu from '../shared/ActionsMenu'
import ReportCommunityModal from './ReportCommunityModal'
import NameOwnerModal from './NameOwnerModal'

export default {
  name: 'CommunityTile',
  components: {
    VImage,
    UserPhoto,
    VButton,
    ReportCommunityModal,
    ActionsMenu,
    AdminCommunityActionsMenu,
    NameOwnerModal
  },
  directives: {
    ClickOutside
  },
  props: {
    community: {
      type: Object,
      required: true
    }
  },
  created() {
    this.checkAdminDisplay()
  },
  data() {
    return {
      showReport: false,
      isReportedCommunity: false
    }
  },
  methods: {
    communityDescription(description) {
      if (description.length > 140) {
        let trimmedDescription = description.substring(0, 140)
        return trimmedDescription.substr(0, Math.min(trimmedDescription.length, trimmedDescription.lastIndexOf(' '))).concat(' ...')
      }
      return description
    },
    dateParser(date) {
      return format(date, 'DD/MM/YYYY')
    },
    addJoinRequest() {
      this.$store.dispatch('addJoinRequest', this.community.id)
    },
    goToCommunity() {
      if (this.hasCommunityAccess() && !this.showReport) {
        this.$router.push({ name: 'communityPage', params: { id: this.community.id } })
      }
    },
    hasCommunityAccess() {
      if (this.community.type.toLowerCase() === 'private') {
        return this.$store.getters.userCommunities.map(userCommunity => userCommunity.idCommunity).includes(this.community.id)
      }
      return true
    },
    checkAdminDisplay() {
      if (this.$store.getters.activeRoute.fullPath.split('/').length > 1) {
        this.isReportedCommunity = this.$store.getters.auth.isAdmin && this.$store.getters.activeRoute.fullPath.split('/')[1] === 'administrator'
      }
    }
  },
  computed: {
    hasJoinRequest() {
      const userJoinRequests = this.$store.getters.userJoinRequests
      if (userJoinRequests.length > 0) {
        return userJoinRequests.filter(joinRequest => joinRequest.idCommunity === this.community.id).length > 0
      }
      return false
    },
    isAdministrator() {
      return this.$store.getters.auth.isAdmin
    }
  }
}

</script>

<style scoped lang="scss">
.container{
  width: 100%;
}
.footer {
  width: 396px;
  height: 222px;

  @media (max-width: $header-change) {
       width: 100%;
  }

  .community-illustration {
    object-fit: cover;
    width: 100%;
    height: 222px;
  }

  .community-join {
    display: none;
    height: 100%;
    background-color: white;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    span {
      color: $black-color;
      font-size: 16px;
      margin-bottom: 2rem;
    }
  }
}

.tile-content:hover .footer {
  .community-join {
    display: flex;
  }

  // hide illustration only if it follows a join block, else, keep visible
  .community-join + .community-illustration {
    display: none;
  }
}

.private {
  background-color: rgba(80, 80, 80, 0.2);
}

.tile-content {
  display: flex;
  flex-direction: column;
  width: 396px;
  position: relative;

  @media (max-width: $header-change) {
     max-width: 100%;
  }

  .tile-header {
    display: flex;
    justify-content: flex-start;

    .private-icon-container {
      display: flex;
      align-items: flex-start;
      margin: 0.5rem;

      position: absolute;
      left: 0;

      img {
        margin-right: 5px;
        height: 22px;
        width: 22px;
      }
    }
  }

  .title {
    font-weight: 700;
    margin: 0.5rem 1rem 1rem 1rem;
    font-size: 18px;
    height: 54px;
    width: 100%;
    padding: 0 40px 0 40px;

    &.reported {
      color: $cerise-color;
    }
  }

  .description {
    text-align: left;
    margin: 0 1rem 1rem 1rem;
    font-size: 15px;
    height: 66px;

    &.reported {
      color: $cerise-color;
    }
  }

  .owner-info {
    height: 52px;
    display: flex;
    margin: 0 1rem;
    position: relative;
    top: 10px;
    z-index: 2;

    .photo-container {
      width: 48px;
      height: 48px;

      .owner-photo {
        border: 2px solid white;
        width: 48px;
        height: 48px;
      }
    }

    .info {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      font-size: 13px;
      margin-left: 0.75rem;

      .bottom {
        font-weight: 700;
      }
    }
  }
}

</style>
